<template>
  <div data-testid="page-invitation-quota">
    <div v-if="loading && !invitationQuota">
      <h2>{{ t('InvitationQuota.loading') }}</h2>
      <ProgressBar />
    </div>
    <AlertBox v-else-if="$cannot('viewTickets', { for: 'User' })" type="warning" class="mt-5">
      {{ t('PartnerCompany.noPartnerCompany') }}
    </AlertBox>
    <div v-else-if="isUnsupportedInvitationQuota">
      <ProgressBar />
    </div>
    <div v-else>
      <h1>{{ title }}</h1>

      <div class="flex justify-between py-3">
        <BackLink :to="backLinkTarget">
          {{ t('InvitationQuota.back') }}
        </BackLink>
        <a v-if="hasGuideUrl" target="_blank" :href="guideUrl" class="inline-flex items-center text-main">
          <SvgIcon :path="mdiHelpCircle" class="mr-2 mb-0.5" :size="17" />
          <span>{{ t('EmailInvitationQuota.tutorialTitle') }}</span>
        </a>
      </div>
      <RouterView :invitation-quota="invitationQuota" :loading="loading" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { mdiHelpCircle } from '@mdi/js'
import { computed, provide, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { useInvitationQuotaQuery, InvitationQuotaTypeEnum } from '@/gql/myomr'
import { toGlobalId, toTypeAndId } from '@/helpers/globalIdHelper'
import { isSupportedInvitationQuota } from '@/helpers/invitationQuotas'
import { getLocalizedAttribute } from '@/helpers/localizedAttribute'
import { $cannot } from '@/services/roles'
import { reloadQuotaKey } from '@/symbols/reloadQuotaKey'

const i18n = useI18n()
const t = i18n.t
const route = useRoute()
const router = useRouter()

const props = defineProps<{
  invitationQuotaId: string
}>()

const { result, loading, refetch } = useInvitationQuotaQuery({ id: toGlobalId('InvitationQuota', props.invitationQuotaId) })

const guideUrls = {
  // TODO: replace with new guide urls once they are available - pascal and mixi
  // [InvitationQuotaTypeEnum.ExhibitorQuota]: '/assets/files/guides/exhibitor_quota.pdf',
  // [InvitationQuotaTypeEnum.CrewQuota]: '/assets/files/guides/crew-quota.pdf',
} as const
const hasGuideUrl = computed(
  () => invitationQuota.value != null && Object.prototype.hasOwnProperty.call(guideUrls, invitationQuota.value.kind),
)
const guideUrl = computed(() => (hasGuideUrl.value ? guideUrls[invitationQuota.value?.kind as keyof typeof guideUrls] : ''))

const invitationQuota = computed(() => result.value?.invitationQuota)
const isUnsupportedInvitationQuota = computed(() => invitationQuota.value != null && !isSupportedInvitationQuota(invitationQuota.value))
const invitationsRouteTarget = computed(() => {
  if (plainEventId.value) {
    return {
      name: 'invitations',
      params: { eventId: plainEventId.value },
    }
  }

  return { name: 'tickets' }
})

const backLinkTarget = computed(() => {
  if (route.name == 'invitationQuota') {
    return invitationsRouteTarget.value
  } else if (route.name == 'invitationQuotaCsvMappingForm') {
    return { name: 'invitationQuotaCsvUpload' }
  } else {
    return { name: 'invitationQuota' }
  }
})

const title = computed(() => {
  if (!invitationQuota.value || invitationQuota.value.kind !== InvitationQuotaTypeEnum.CodeQuota) {
    return t('InvitationQuota.yourInvites')
  }

  // We have a CodeQuota
  if (invitationQuota.value.title) {
    return invitationQuota.value.title
  }

  const ticketCategory = invitationQuota.value.ticketCategory
  return getLocalizedAttribute({
    de: ticketCategory?.titleDe,
    en: ticketCategory?.titleEn,
    fallback: '',
    locale: i18n.locale.value,
  })
})

const plainEventId = computed(() => {
  return (invitationQuota.value?.event && toTypeAndId(invitationQuota.value.event.id)['id']) || ''
})

watch(
  invitationQuota,
  (quota) => {
    if (quota && !isSupportedInvitationQuota(quota)) {
      router.replace(invitationsRouteTarget.value)
    }
  },
  { immediate: true },
)

provide(reloadQuotaKey, refetch)
</script>
